import React from 'react';
import { Bell, Check, CheckCheck, ExternalLink, AlertCircle, Info, X, ArrowRight, Play } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const NotificationsPanel = ({ 
  notifications = [], 
  onClose, 
  onMarkAsRead, 
  onViewFinding,
  onMarkAllAsRead,
  position = { top: 64, right: 16 }
}) => {
  const navigate = useNavigate();
  const unreadCount = notifications.filter(n => !n.read).length;
  
  const handleViewAll = () => {
    onClose?.();
    navigate('/notifications');
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.read && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }

    // Navigate based on notification content
    if (notification.finding_id && onViewFinding) {
      // If there's a finding, view it
      onViewFinding(notification.finding_id);
    } else if (notification.runner_id) {
      // If there's a runner, navigate to its results
      onClose?.();
      navigate(`/results/${notification.runner_id}`);
    }
  };
  
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'finding':
        return <AlertCircle size={16} className="text-accent" />;
      case 'info':
        return <Info size={16} className="text-info" />;
      default:
        return <Bell size={16} className="text-ink-tertiary" />;
    }
  };

  return (
    <div 
      className="fixed w-96 max-h-[70vh] bg-surface-secondary border border-border rounded-lg shadow-dropdown overflow-hidden z-[99999] flex flex-col"
      style={{
        top: `${position.top}px`,
        right: `${position.right}px`
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-surface-tertiary">
        <div className="flex items-center gap-2">
          <Bell size={18} className="text-ink-secondary" />
          <h3 className="text-title text-ink">Notifications</h3>
          {unreadCount > 0 && (
            <span className="px-1.5 py-0.5 bg-accent text-white text-tiny font-medium rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={onMarkAllAsRead}
              className="flex items-center gap-1 px-2 py-1 text-tiny text-accent hover:bg-accent-soft rounded transition-base"
            >
              <CheckCheck size={14} />
              Mark all read
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1 text-ink-tertiary hover:text-ink hover:bg-surface rounded transition-base"
          >
            <X size={16} />
          </button>
        </div>
      </div>
      
      {/* Content - flex-1 allows it to grow but respects the parent max-height */}
      <div className="overflow-y-auto flex-1 min-h-0">
        {notifications.length === 0 ? (
          <div className="py-12 text-center">
            <Bell size={32} className="mx-auto text-ink-muted mb-3" />
            <p className="text-body text-ink-secondary">No notifications</p>
            <p className="text-caption text-ink-tertiary mt-1">You're all caught up!</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 hover:bg-surface-tertiary transition-base cursor-pointer ${
                  !notification.read ? 'bg-accent-soft/30' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex gap-3">
                  <div className="shrink-0 mt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-body ${!notification.read ? 'text-ink font-medium' : 'text-ink-secondary'}`}>
                      {notification.title || notification.message}
                    </p>
                    {notification.description && (
                      <p className="text-caption text-ink-tertiary mt-1 line-clamp-2">
                        {notification.description}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-tiny text-ink-tertiary">
                        {notification.created_at 
                          ? formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })
                          : 'Just now'
                        }
                      </span>
                      {notification.finding_id && (
                        <span className="flex items-center gap-1 text-tiny text-accent">
                          <ExternalLink size={12} />
                          View finding
                        </span>
                      )}
                      {!notification.finding_id && notification.runner_id && (
                        <span className="flex items-center gap-1 text-tiny text-accent">
                          <Play size={12} />
                          View runner
                        </span>
                      )}
                    </div>
                  </div>
                  {!notification.read && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onMarkAsRead?.(notification.id);
                      }}
                      className="shrink-0 p-1 text-ink-tertiary hover:text-success transition-base"
                      title="Mark as read"
                    >
                      <Check size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Footer with link to all notifications - shrink-0 ensures it's always visible */}
      <div className="shrink-0 border-t border-border bg-surface-tertiary px-4 py-3">
        <button
          onClick={handleViewAll}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-body text-accent hover:bg-accent-soft rounded transition-base font-medium"
        >
          <span>View all notifications</span>
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default NotificationsPanel;
